/*
 * Le composant calendrier (BBMCalendar) supporte 3 langues (fr, en, de), 4 vues (jour, semaine
 * travaillée, semaine complète et mois) et son contenu est extensible, avec pour le moment :
 * - l'activité quotidienne (BBMCalendarActivity)
 * - les notes de frais (BBMCalendarExpense)
 * - l'agenda, le plus utilisé (BBMCalendarPlanner)
 *
 * Lancer "jquery-calendar.html" pour avoir une idée du fonctionnement de ce composant.
 */
function buildCalendarAPI($, moment) {
	"use strict";

	var frenchLocale = {
		formatTime: function(timeMS) {
			return timeMS ? moment(timeMS).format('HH[h]mm') : '';
		},
		formatDuration: function(hours, minutes) {
			return (hours > 0 ? (hours + 'h ') : '') + minutes + 'm';
		},
		wholeDayLabel: 'Toute la journée',
		weekFormat: '[Semaine] W, MMMM GGGG',
		addEventLabel: 'Ajouter un événement',
		freeSlot: 'Créneau libre',
		formatEventCount: function(count) {
			return (count === 0) ? 'aucun événement' : (count === 1) ? 'un événement' : (count + ' événements');
		},
		notEnoughtBreakTimeFormatMessage: function(hours, dateMS) {
			return 'Il y a moins de ' + hours + 'h de repos avant le premier événement du ' + moment(dateMS).format('DD/MM/YYYY') + '.';
		},
		tooManyWorkingTimePerDayMessage: function(hours, dateMS) {
			return 'Il y a plus de ' + hours + 'h travaillées le ' + moment(dateMS).format('DD/MM/YYYY') + '.';
		}
	};

	var englishLocale = {
		formatTime: function(timeMS) {
			return timeMS ? moment(timeMS).format('hh:mm a') : '';
		},
		formatDuration: function(hours, minutes) {
			return (hours > 0 ? (hours + 'h ') : '') + minutes + 'm';
		},
		wholeDayLabel: 'Whole day',
		weekFormat: '[Week] W, MMMM GGGG',
		addEventLabel: 'Add a new event',
		freeSlot: 'Free slot',
		formatEventCount: function(count) {
			return (count === 0) ? 'no event' : (count === 1) ? 'one event' : (count + ' events');
		},
		notEnoughtBreakTimeFormatMessage: function(hours, dateMS) {
			return 'There are less than ' + hours + ' hours of rest before the first event on ' + moment(dateMS).format('DD/MM/YYYY') + '.';
		},
		tooManyWorkingTimePerDayMessage: function(hours, dateMS) {
			return 'There is more than ' + hours + 'hours worked on ' + moment(dateMS).format('MM/DD/YYYY') + '.';
		}
	};

	var germanLocale = {
		formatTime: function(timeMS) {
			return timeMS ? moment(timeMS).format('HH:mm') : '';
		},
		formatDuration: function(hours, minutes) {
			return (hours > 0 ? (hours + 's ') : '') + minutes + 'm';
		},
		wholeDayLabel: 'Ganz Tag',
		weekFormat: '[Woche] W, MMMM GGGG',
		addEventLabel: 'ein Ereignis hinzufügen',
		freeSlot: 'Freier Steckplatz',
		formatEventCount: function(count) {
			return (count === 0) ? 'kein Ereignis' : (count === 1) ? 'ein Ereignis' : (count + ' Ereignisse');
		},
		notEnoughtBreakTimeFormatMessage: function(hours, dateMS) {
			return 'Bis zur ersten Veranstaltung am ' + moment(dateMS).format('DD.MM.YYYY') + ' sind weniger als ' + hours + ' Stunden Ruhezeit vorgesehen.';
		},
		tooManyWorkingTimePerDayMessage: function(hours, dateMS) {
			return 'Am ' + moment(dateMS).format('DD.MM.YYYY') + ' wurden mehr als ' + hours + ' Stunden gearbeitet.';
		}
	};

	var locales = {
		'fr': frenchLocale,
		'en': englishLocale,
		'de': germanLocale,
	};

	var dayView = {
		name: 'day',
		navigateUnit: 'days',
		singleWeek: true,
		filterDays: false,
		startMoment: function(when, _workedDays) {
			return moment(when).startOf('day');
		},
		endMoment: function(when, _workedDays) {
			return moment(when).endOf('day');
		},
		formatPeriod: function(when, _startMoment, _endMoment) {
			return when.format('L');
		}
	};

	var wweekView = {
		name: 'wweek',
		navigateUnit: 'weeks',
		singleWeek: true,
		filterDays: true,
		startMoment: function(when, workedDays) {
			var m = moment(when).startOf('week');
			if (workedDays) {
				while (!workedDays[m.day()]) {
					m.add(1, 'days');
				}
			}
			return m;
		},
		endMoment: function(when, workedDays) {
			var m = moment(when).endOf('week');
			if (workedDays) {
				while (!workedDays[m.day()]) {
					m.add(-1, 'days');
				}
			}
			return m;
		},
		formatPeriod: function(when, _startMoment, _endMoment) {
			return when.format(CalendarAPI.getLocale(when.locale()).weekFormat);
		}
	};

	var fweekView = {
		name: 'fweek',
		navigateUnit: 'weeks',
		singleWeek: true,
		filterDays: false,
		startMoment: function(when, _workedDays) {
			return moment(when).startOf('week');
		},
		endMoment: function(when, _workedDays) {
			return moment(when).endOf('week');
		},
		formatPeriod: function(when, _startMoment, _endMoment) {
			return when.format(CalendarAPI.getLocale(when.locale()).weekFormat);
		}
	};

	var monthView = {
		name: 'month',
		navigateUnit: 'months',
		singleWeek: false,
		filterDays: false,
		startMoment: function(when, _workedDays) {
			return moment(when).startOf('month').startOf('week');
		},
		endMoment: function(when, _workedDays) {
			return moment(when).endOf('month').endOf('week');
		},
		formatPeriod: function(when, _startMoment, _endMoment) {
			return when.format('MMMM YYYY');
		}
	};

	var views = {
		'day': dayView,
		'wweek': wweekView,
		'fweek': fweekView,
		'month': monthView
	};

	function formatDuration(lang, milliseconds) {
		var minutes = milliseconds / 60000;
		var hours = Math.floor(minutes / 60);
		minutes = Math.round(minutes % 60);
		if (typeof lang === 'string')
			lang = CalendarAPI.getLocale(lang);
		return lang.formatDuration(hours, minutes);
	}

	function BBMCalendarActivity(target, options) {
		// Settings { sections: { section: {title: '', iconURL: ''}, ... }, fields: { field: {title: '', visible: ?}, ... } }
		// Activity { timeMS: ms, startKilometers: Integer, endKilometers: Integer, privateKilometers: Integer, kilometers: Integer, totalKilometers, hotel: Boolean, startTown: String, endTown: String, startTime: ms, endTime: ms, comment: String, startBreak: ms, endBreak: ms }
	
		// Le paramétrage, sous la forme d'un pseudo-objet Settings
		this.settings = options.settings;
		// Une function(startMoment, endMoment, callback([pseudo-objet Activity])) chargée de la récupération de l'activité
		this.onquery = options.onquery;
		// Une function(moment) indiquant si l'activité d'une date en particulier peut être modifiée
		this.oneditable = options.oneditable;
		// Une fonction(moment, onmodified) appelant quand l'utilisateur clique sur un jour
		this.onedit = options.onedit;
		// Un template pour le bouton d'ajout
		this.addTemplate = options.addTemplate || '<button type="button" class="btn"><i class="material-icons">edit</i></button>';
		// Un template pour le bouton de modification
		this.editTemplate = options.editTemplate || '<button type="button" class="btn"><i class="material-icons">edit</i></button>';
		// Un template pour la coche "hotel" cochée
		this.checkedTemplate = options.checkedTemplate || '<i class="material-icons" title="checked">check_box</i>';
		// Un template pour la coche "hotel" cochée
		this.uncheckedTemplate = options.uncheckedTemplate || '';
		// Classe pour les lignes d'activité dans le calendrier
		this.rowClass = 'activity';
		// Détecter les clics sur les boutons d'édition d'activité
		target.off('click', '.' + this.rowClass +' button').on('click', '.' + this.rowClass +' button', this.edit.bind(this));
	}

	BBMCalendarActivity.prototype.edit = function(event) {
		var day = $(event.target).closest('[data-date]').attr('data-date');
		var dayMoment = moment(day, CalendarAPI.dateAttributeFormat);
		this.onedit(dayMoment);
	};

	BBMCalendarActivity.prototype.queryData = function(startMoment, endMoment, callbackWithData) {
		// Pour l'activité, c'est facile, il suffit d'appeler "onquery"
		this.onquery(startMoment, endMoment, callbackWithData);
	};

	BBMCalendarActivity.prototype.generateCell = function(cell, list) {
		var day = cell.attr('data-date');
		var dayMoment = moment(day, CalendarAPI.dateAttributeFormat);
		var dayEditable = this.oneditable(dayMoment);
		var dayEntry = list.find(function(entry) { return dayMoment.isSame(entry.timeMS, 'day'); });
		var lang;
	
		// Ajouter l'icône d'une section, avec une propriété "title" utile
		function show(section, title) {
			if (section.iconName) {
				$('<span class="material-icons"></span>').text(section.iconName);
			}
			else{
				$('<img />').attr('title', title || section.title).attr('src', section.iconURL).appendTo(cell);
			}
		}
		// Ajouter l'icône d'une section, avec une propriété "title" affichant un interval
		function showInterval(section, s, e) {
			show(section, s + (s || e ? ' ... ' : '') + e);
		}
		
		function createTitle(values) {
			var title = '';
			values.forEach(function(value) {
				title += (title.length > 0  ? '\n' : '') + value;
			});
			return title;
		}
	
		cell.toggleClass('editable', dayEditable);
	
		if (cell.parent().is('.total')) {
			// 1. TOTAUX PAR JOUR
	
			// Vider la cellule
			cell.empty();
			if (dayEntry) {
				// Indiquer que le jour contient des données
				cell.removeClass('empty')
					// Ajouter le bouton de consultation / modification
					.append($(this.editTemplate))
					// Afficher le kilométrage total en total
					.append($('<span class="title" />').text((dayEntry.totalKilometers || 0) + ' km'))
					// Afficher le kilométrage perso en sous-total
					.append($('<span class="subtitle" />').text((dayEntry.privateKilometers || 0) + ' km'));
			} else {
				// Indiquer que le jour est pour l'instant vide
				cell.addClass('empty');
				// Ajouter un bouton aux jours vides, à condition qu'ils soient modifiables
				if (dayEditable)
					cell.append($(this.addTemplate));
			}
	
		} else if (cell.closest('.calendar').is('.month')) {
			// 2. DETAIL EN VUE MOIS
	
			// Génération du contenu d'une vue mois : les icônes des catégories avec données
			cell.children(':not(.day)').remove();
			if (dayEntry) {
				// Récupérer l'objet permettant d'ajuster en fonction de la langue
				lang = CalendarAPI.getLocale(moment.locale());
	
				// Commentaire
				// (affiché à droite mais on est en "float: right;" donc on l'ajoute en premier)
				if (this.settings.fields.comment.visible && dayEntry.comment)
					show(this.settings.sections.comment, dayEntry.comment);
	
				// Hôtel
				if (this.settings.fields.hotel.visible && dayEntry.hotel)
					show(this.settings.sections.hotel);
	
				// Heure de début et fin
				if (this.settings.fields.startTime.visible && dayEntry.startTime
						|| this.settings.fields.endTime.visible && dayEntry.endTime)
					showInterval(this.settings.sections.times, lang.formatTime(dayEntry.startTime), lang.formatTime(dayEntry.endTime));
	
				// Ville de départ et d'arrivée
				if (this.settings.fields.startTown.visible && dayEntry.startTown
						|| this.settings.fields.endTown.visible && dayEntry.endTown)
					showInterval(this.settings.sections.towns, dayEntry.startTown || '', dayEntry.endTown || '');
	
				// Kilométrage
				if (this.settings.fields.totalKilometers.visible && dayEntry.totalKilometers
						|| this.settings.fields.startKilometers.visible && dayEntry.startKilometers
						|| this.settings.fields.endKilometers.visible && dayEntry.endKilometers
						|| this.settings.fields.privateKilometers.visible && dayEntry.privateKilometers
						|| this.settings.fields.kilometers.visible && dayEntry.kilometers) {
					var title = [];
					if (this.settings.fields.startKilometers.visible) 
						title.push(this.settings.fields.startKilometers.title + ' : ' + (dayEntry.startKilometers || 0) + ' km');
					if (this.settings.fields.endKilometers.visible) 
						title.push(this.settings.fields.endKilometers.title + ' : ' + (dayEntry.endKilometers || 0) + ' km');
					if (this.settings.fields.kilometers.visible) 
						title.push(this.settings.fields.kilometers.title + ' : ' + (dayEntry.kilometers || 0) + ' km');
					if (this.settings.fields.privateKilometers.visible) 
						title.push(this.settings.fields.privateKilometers.title + ' : ' + (dayEntry.privateKilometers || 0) + ' km');
					if (this.settings.fields.totalKilometers.visible)
						title.push(this.settings.fields.totalKilometers.title + ' : ' + (dayEntry.totalKilometers 
							|| ((dayEntry.kilometers && dayEntry.privateKilometers) ? (dayEntry.kilometers + dayEntry.privateKilometers) : 0)
							|| ((dayEntry.endKilometers && dayEntry.startKilometers) ? (dayEntry.endKilometers - dayEntry.startKilometers) : 0)) + ' km');
					show(this.settings.sections.kilometers, createTitle(title));	
				}
				// Début et fin de pause
				if (this.settings.fields.startBreak.visible && dayEntry.startBreak
						|| this.settings.fields.endBreak.visible && dayEntry.endBreak)
					showInterval(this.settings.sections.breaks, lang.formatTime(dayEntry.startBreak), lang.formatTime(dayEntry.endBreak));
			}
	
		} else {
			// 3. DETAIL EN VUE JOUR(s)
	
			// Vider la cellule
			cell.empty();
			if (dayEntry) {
				// Récupérer l'objet permettant d'ajuster en fonction de la langue
				lang = CalendarAPI.getLocale(moment.locale());
	
				// Générer le contenu du jour dans une table
				var cellTable = $('<table />').appendTo(cell);
	
				// La génération d'une section (km, villes, heures, hotel ou commentaire)
				var add = function(section, tuples) {
					// Compter les champs visibles
					var visibleTuples = [];
					tuples.forEach(function(tuple) {
						if (tuple.field.visible && tuple.value)
							visibleTuples.push(tuple);
					});
	
					// Si la section est vide, on ne l'affiche pas
					if (visibleTuples.length === 0)
						return;
	
					// Si la section ne contient qu'un champ, on affiche en une ligne
					if (visibleTuples.length === 1) {
						$('<tr class="section" />')
							.append($('<td class="icon"></td>').append(section.iconName ? '<span class="material-icons" style="color: #FF8983;">' + section.iconName + ' </span>' : '<img src="' + section.iconURL + '" />')) // icone de la section
							.append('<td class="title">' + visibleTuples[0].field.title + '</td>') // mais label du champ car tout seul
							.append('<td class="value">' + visibleTuples[0].value + '</td>')
							.appendTo(cellTable);
	
					} else {
						// On affiche l'en-tête de la section, avec l'icone et le titre
						$('<tr class="section" />')
							.append($('<td class="icon" rowspan="' + (visibleTuples.length + 1) + '"></td>').append(section.iconName ? '<span class="material-icons" style="color: #FF8983;">' + section.iconName + ' </span>' : '<img src="' + section.iconURL + '" />'))
							.append('<td class="title">' + section.title + '</td>')
							.append('<td>&nbsp;</td>')
							.appendTo(cellTable);
	
						// On affiche ensuite le contenu de la section
						visibleTuples.forEach(function(tuple) {
							$('<tr class="entry" />')
								.append('<td class="title">' + tuple.field.title + '</td>')
								.append('<td class="value">' + tuple.value + '</td>')
								.appendTo(cellTable);
						});
					}
	
					// On ajoute une ligne séparatrice
					$('<tr class="separator"><td colspan="3"></td></tr>').appendTo(cellTable);
				};
	
				add(this.settings.sections.kilometers, [
					{ field: this.settings.fields.totalKilometers, value: dayEntry.totalKilometers ? (dayEntry.totalKilometers + ' km') : '' },
					{ field: this.settings.fields.startKilometers, value: dayEntry.startKilometers ? (dayEntry.startKilometers + ' km') : ''},
					{ field: this.settings.fields.endKilometers, value: dayEntry.endKilometers ? (dayEntry.endKilometers + ' km') : ''},
					{ field: this.settings.fields.kilometers, value: dayEntry.kilometers ? (dayEntry.kilometers + ' km') : ''},
					{ field: this.settings.fields.privateKilometers, value: dayEntry.privateKilometers ? (dayEntry.privateKilometers + ' km') : ''}
				]);
				add(this.settings.sections.towns, [
					{ field: this.settings.fields.startTown, value: dayEntry.startTown || ''},
					{ field: this.settings.fields.endTown, value: dayEntry.endTown || ''}
				]);
				add(this.settings.sections.times, [
					{ field: this.settings.fields.startTime, value: lang.formatTime(dayEntry.startTime)},
					{ field: this.settings.fields.endTime, value: lang.formatTime(dayEntry.endTime)}
				]);
				add(this.settings.sections.breaks, [
					{ field: this.settings.fields.startBreak, value: lang.formatTime(dayEntry.startBreak)},
					{ field: this.settings.fields.endBreak, value: lang.formatTime(dayEntry.endBreak)}
				]);
				add(this.settings.sections.hotel, [
					{ field: this.settings.fields.hotel, value: dayEntry.hotel ? this.checkedTemplate : this.uncheckedTemplate }
				]);
	
				// Pour "Commentaire", c'est un peu différent on affiche juste l'icone et le commentaire
				if (this.settings.fields.comment.visible && dayEntry.comment) {
					$('<tr class="entry" />')
						.append('<td class="icon"><img src="' + this.settings.sections.comment.iconURL + '" /></td>') // icone de la section commentaire
						.append('<td colspan="2">' + dayEntry.comment + '</td>') // mais commentaire direct en colspan=2
						.appendTo(cellTable);
				}
				
				add(this.settings.sections.carbonFootprints, [
					{ field: this.settings.fields.optimalCo2, value: dayEntry.optimalCo2 ? (dayEntry.optimalCo2 + ' g') : '' },
					{ field: this.settings.fields.optimalDistance, value: dayEntry.optimalDistance ? (dayEntry.optimalDistance + ' km') : '' },
					{ field: this.settings.fields.optimalTravelTime, value: dayEntry.optimalTravelTime ? (dayEntry.optimalTravelTime + ' s') : '' }
				]);
			}
		}
	};

	function BBMCalendarExpense(target, options) {
		// ExpenseEntry { typeId:Number, timeMS:ms, ht:Number, ttc:Number }
		// ExpenseType { id:Number, title:String }
		// ExpenseCategory { title:String, iconURL:String, types: [ExpenseType] }
	
		// Une tableau de ExpenseCategory
		this.categories = options.categories;
		// Une function(startMoment, endMoment, callback([ExpenseEntry])) chargée de la récupération des frais
		this.onquery = options.onquery;
		// Une function(moment) indiquant si les frais d'une date en particulier peuvent être modifiés
		this.oneditable = options.oneditable;
		// Une fonction(moment, onmodified) appelant quand l'utilisateur clique sur un jour
		this.onedit = options.onedit;
		// Un template pour le bouton d'ajout
		this.addTemplate = options.addTemplate || '<button type="button" class="btn"><i class="material-icons">edit</i></button>';
		// Un template pour le bouton de modification
		this.editTemplate = options.editTemplate || '<button type="button" class="btn"><i class="material-icons">edit</i></button>';
		// Classe pour les lignes de frais dans le calendrier
		this.rowClass = 'expense';
		// Détecter les clics sur les boutons d'édition des frais
		target.off('click', '.' + this.rowClass +' button').on('click', '.' + this.rowClass +' button', this.edit.bind(this));
	}

	BBMCalendarExpense.prototype.edit = function(event) {
		var day = $(event.target).closest('[data-date]').attr('data-date');
		var dayMoment = moment(day, CalendarAPI.dateAttributeFormat);
		this.onedit(dayMoment);
	};

	BBMCalendarExpense.prototype.queryData = function(startMoment, endMoment, callbackWithData) {
		var self = this;
		self.onquery(startMoment, endMoment, function(data) {
			// Avant d'appeler "callbackWithData", on va transformer "data" :
			// - en entrée, on a un tableau de ExpenseEntry { typeId:Number, timeMS:ms, ht:Number, ttc:Number }
			// - en sortie, on a un tableau trié (par jour -> catégorie -> type) d'objets { moment, day, typeIndex, categoryIndex, ht, ttc }
	
			// On crée une map pour retrouver la position d'une catégorie ou d'un type dans une catégorie
			var typeIndexByTypeId = {};
			var categoryIndexByTypeId = {};
			self.categories.forEach(function(category, categoryIndex) {
				category.types.forEach(function(type, typeIndex) {
					typeIndexByTypeId['t' + type.id] = typeIndex;
					categoryIndexByTypeId['t' + type.id] = categoryIndex;
				});
			});
	
			// Pour chaque entrée de "data", on récupère le jour, l'index de la categorie et l'index du type dans la catégorie
			var list = data.map(function(entry) {
				var m = moment(entry.timeMS);
				return {
					moment: m,
					day: m.format(CalendarAPI.dateAttributeFormat),
					typeIndex: typeIndexByTypeId['t' + entry.typeId],
					categoryIndex: categoryIndexByTypeId['t' + entry.typeId],
					ht: entry.ht,
					ttc: entry.ttc
				};
			});
	
			// On trie les entrées par jour, catégorie et type pour optimiser "generateCell"
			list.sort(function(e1, e2) {
				var compare = e1.moment.diff(e2.moment);
				if (compare === 0) {
					compare = e1.categoryIndex - e2.categoryIndex;
					if (compare === 0)
						compare = e1.typeIndex - e2.typeIndex;
				}
				return compare;
			});
	
			// On retourne la liste transformée et triée
			callbackWithData(list);
		});
	};

	BBMCalendarExpense.prototype.generateCell = function(cell, list) {
		var day = cell.attr('data-date');
		var dayMoment = moment(day, CalendarAPI.dateAttributeFormat);
		var dayEditable = this.oneditable(dayMoment);
		cell.toggleClass('editable', dayEditable);
	
		if (cell.parent().is('.total')) {
			// 1. TOTAUX PAR JOUR
	
			// Calculer les totaux HT et TTC du jour
			var dayHT = 0,
				dayTTC = 0;
			list.forEach(function(entry) {
				if (entry.day === day) {
					dayHT += entry.ht || 0;
					dayTTC += entry.ttc || 0;
				}
			});
			// Vider la cellule
			cell.empty();
			if (dayHT !== 0 || dayTTC !== 0) {
				// Indiquer que le jour contient des données
				cell.removeClass('empty')
					// Ajouter le bouton de consultation / modification
					.append($(this.editTemplate))
					// Ajouter le total TTC en grand
					.append($('<span class="title" />').text(dayTTC.toFixed(2) + ' €'))
					// Ajouter le total HT en plus petit
					.append($('<span class="subtitle" />').text(dayHT.toFixed(2) + ' € HT'));
			} else {
				// Indiquer que le jour est pour l'instant vide
				cell.addClass('empty');
				// Ajouter un bouton aux jours vides, à condition qu'ils soient modifiables
				if (dayEditable)
					cell.append($(this.addTemplate));
			}
	
		} else if (cell.closest('.calendar').is('.month')) {
			// 2. DETAIL EN VUE MOIS
	
			// Vider la cellule en conservant tout de même le numéro du jour
			cell.children(':not(.day)').remove();
	
			// Calculer les totaux TTC et HT par index de catégorie
			var categoryValuesByCategoryIndex = {};
			list.forEach(function(entry) {
				if (entry.day === day) {
					var values = categoryValuesByCategoryIndex['' + entry.categoryIndex];
					if (! values)
						values = categoryValuesByCategoryIndex['' + entry.categoryIndex] = { ht: 0, ttc: 0 };
					values.ht = values.ht + (entry.ht || 0);
					values.ttc = values.ttc + (entry.ttc || 0);
				}
			});
	
			// Génération du contenu d'une vue mois : les icônes des catégories avec données avec les montants en "title"
			this.categories.forEach(function(category, categoryIndex) {
				var values = categoryValuesByCategoryIndex['' + categoryIndex];
				if (values) {
					var ttc = values.ttc ? (values.ttc.toFixed(2) + ' € TTC') : '';
					var ht = values.ht ? (values.ht.toFixed(2) + ' € HT') : '';
					var title = ttc + (ttc && ht ? ' (' : '') + ht + (ttc && ht ? ')' : '');
					$('<img />').attr('src', category.iconURL).attr('title', title).appendTo(cell);
				}
			});
	
		} else {
			// 3. DETAIL EN VUE JOUR(s)
	
			// Avancer jusqu'au jour
			var i = 0;
			while (i < list.length && list[i].day < day) {
				i++;
			}
	
			// Générer le contenu du jour dans une table
			var cellTable = $('<table />').appendTo(cell.empty());
			this.categories.forEach(function(category, categoryIndex) {
				var categoryTTC = 0,
					categoryEntries = [];
	
				category.types.forEach(function(type, typeIndex) {
					var typeHT = 0,
						typeTTC = 0;
	
					while (i < list.length && list[i].day === day && list[i].categoryIndex === categoryIndex && list[i].typeIndex === typeIndex) {
						typeHT += list[i].ht || 0;
						typeTTC += list[i].ttc || 0;
						categoryTTC += typeTTC;
						i++;
					}
					if (typeHT !== 0 || typeTTC !== 0)
						categoryEntries.push({
							type: type,
							ht: typeHT,
							ttc: typeTTC
						});
				});
	
				if (categoryEntries.length > 0) {
					$('<tr class="section" />').appendTo(cellTable)
						.append('<td class="icon" rowspan="' + (categoryEntries.length + 1) + '"><img src="' + category.iconURL + '" /></td>')
						.append('<td class="title">' + category.title + '</td>')
						.append('<td class="value">' + categoryTTC.toFixed(2) + ' €</td>');
					categoryEntries.forEach(function(e) {
						$('<tr class="entry" />').appendTo(cellTable)
							.append('<td class="title">' + e.type.title + '</td>')
							.append('<td class="value">' + e.ttc.toFixed(2) + ' €</td>');
					});
					$('<tr class="separator"><td colspan="3"></td></tr>').appendTo(cellTable);
				}
			});
		}
	};

	function BBMCalendarPlanner(target, options) {
		// EventType { id: Integer, caption: String, color:css }
		// EventProperty { caption: String, canWrap: boolean, format: function(event, type, lang):String }
		// Event { id: Integer, typeId, Integer, caption: String, allDay: Boolean, startTime: ms, endTime: ms, done: Boolean, data: any }
	
		this.target = target;
		// Un tableau de EventType
		this.types = options.types;
		// Un tableau de EventProperty
		this.properties = options.properties || [];
		// Une function(startMoment, endMoment, callback([Event])) chargée de la récupération de l'agenda
		this.onquery = options.onquery;
		// Une fonction(event) appelée quand l'utilisateur sélectionne un événement en vue semaine
		this.onselect = options.onselect;
		// Une fonction(eventId, when) appelée quand l'utilisateur veut ajouter ou éditer un événement
		this.onedit = options.onedit;
		// Une function() indiquant si la journée accepte l'ajout d'événements
		this.isinsertable = options.isinsertable;
		// Un booléen pour choisir la taille par defaut des event en vue jour
		this.fixedEventSize = options.fixedEventSize;
		// Un objet moment pour le début de la journée
		this.dayStart = options.dayStart || moment('08:00', 'HH:mm');
		// Un objet moment pour la fin de la journée
		this.dayEnd = moment('00:00', 'HH:mm').add(1, 'days');
		// Un template pour le bouton d'ajout
		this.addTemplate = options.addTemplate || '<button type="button" class="btn editevent"><i class="material-icons">add</i></button>';
		// Un template pour le bouton de modification
		this.editTemplate = options.editTemplate || ''
			+ '<div class="btn-group dropdown" style="float:left">'
			+ '  <button type="button" class="btn dropdown-toggle" data-toggle="dropdown"><i class="material-icons">edit</i></button>'
			+ '  <ul class="dropdown-menu" role="menu"></ul>'
			+ '</div>';
		// Un template pour le bouton d'ajout en vue mois (dans la dropdown)
		this.addInDropdownTemplate = options.addInDropdownTemplate || '<i class="material-icons">add</i>';
		// Un template pour les élement de la liste de dropdown
		this.dropdownLiTemplate = options.dropdownLiTemplate || '<li class="editevent"><a><span></span><b></b></a></li>';
		// Un template pour ajouter des informations dans la case des totaux. Fonction qui prend en paramètre la date et qui retourne un élément jquery
		this.extraSubtitleTemplate = options.extraSubtitleTemplate;
		// Classe pour les lignes agenda dans le calendrier
		this.rowClass = 'planner';
		this.showGhostEvents = options.showGhostEvents || false;
		this.minBreakTimeBetweenWorkingDays = options.minBreakTimeBetweenWorkingDays;
		this.maxWorkingTimePerDay = options.maxWorkingTimePerDay;
		this.lastEventEndTimePreviousDay = undefined;
		this.showError = options.showError;
		// Fonction pour filtrer les événements à comptabiliser dans les totaux
		this.totalFilterFunction = options.totalFilterFunction; 
		// Détecter le click sur les boutons d'édition d'événement
		target.off('click', '.' + this.rowClass + ' .editevent').on('click', '.' + this.rowClass + ' .editevent:not(.event-ghost)', this.edit.bind(this));
		// Détecter le click sur un event
		target.off('click', '.' + this.rowClass + ' .plannerEvent').on('click', '.' + this.rowClass + ' .plannerEvent:not(.event-ghost)', function(){
			var self = $(this);
			target.find('.active').removeClass('active');
			self.addClass('active');
			options.onselect(self.data('event'));
		});
		// Détecter le doubleclick sur un event
		target.off('dbltap', '.' + this.rowClass + ' .plannerEvent').on('dbltap', '.' + this.rowClass + ' .plannerEvent:not(.event-ghost)', this.edit.bind(this));
	}

	BBMCalendarPlanner.prototype.edit = function(event) {
		var dom = $(event.currentTarget);
		var day = dom.closest('[data-date]').attr('data-date');
		var dayMoment = moment(day, CalendarAPI.dateAttributeFormat);
		this.onedit(dom.attr('data-id'), dayMoment);	
	};

	BBMCalendarPlanner.prototype.queryData = function(startMoment, endMoment, callbackWithData) {
		var self = this;
		self.onquery(startMoment, endMoment, function(entries) {
			// Avant d'appeler "callbackWithData", on va transformer "data" :
			// - en entrée, on a un tableau de Event {  }
			// - en sortie, on a un tableau trié (par allDay -> début -> fin) d'objets { id, typeIndex, startTime, endTime, caption, allDay, done, data }
	
			// On crée une map pour retrouver la position d'un type
			var typeIndexByTypeId = {};
			self.types.forEach(function(type, typeIndex) {
				typeIndexByTypeId['t' + type.id] = typeIndex;
			});
	
			// Pour chaque entrée, on récupère le jour, l'index du type
			var list = entries.map(function(entry) {
				var typeIndex = typeIndexByTypeId['t' + entry.typeId];
				var endTimeMoment = moment(entry.endTime);
				return {
					id: entry.id,
					typeIndex: typeIndex,
					startTime: moment(entry.startTime),
					endTime: entry.allDay ? endTimeMoment.subtract(1, 'days') : endTimeMoment,
					caption: entry.caption,
					allDay: entry.allDay || 0,
					done: entry.done,
					data: entry.data,
					locked: entry.locked,
					warning: entry.warning,
					warningMessage: entry.warningMessage
				};
			});
	
			// On trie les entrées par jour, allDay, heure debut, heure de fin ( <=> duration)
			list.sort(function(e1, e2) {
				//trie par allDay
				//(e2 le plus eleve = true, e1 le plus faible = false <=> compare <0 echange e1 et e2)
				var compare = e2.allDay - e1.allDay;
				//si aucun allDay (ou tous allDay) trie par date de début
				if (compare === 0)
					compare = e1.startTime.diff(e2.startTime);
				//si même début tri par fin
				if (compare === 0)
					compare = e1.endTime.diff(e2.endTime);
				return compare;
			});
	
			// On retourne la liste transformée et triée
			callbackWithData(self.internalFillWithGhostEvents(list));
		});
	};

	BBMCalendarPlanner.prototype.internalFillWithGhostEvents = function(events) {
		var self = this;
		var result = [];

		function beginAtStartOfDay(event) {
			return event.startTime.minutes() === self.dayStart.minutes() && event.startTime.hours() === self.dayStart.hours();
		}

		function areOverlapping(firstEvent, secondEvent) {
			return secondEvent.startTime.isBetween(firstEvent.startTime, firstEvent.endTime, 'minute', '[]')
				|| secondEvent.endTime.isBetween(firstEvent.startTime, firstEvent.endTime, 'minute', '[]');
		}

		function createGhostEvent(startTime, endTime) {
			return { id: -1, typeIndex: -1, startTime: startTime, endTime: endTime, caption: '', allDay: 0, done: false, data: {}, locked: false, ghost: true };
		}

		function getStartOfDay(aMoment) {
			var result =  moment(aMoment.valueOf());
			result.hours(self.dayStart.hours());
			result.minutes(self.dayStart.minutes());
			return result;
		}
		
		if (!events || events.length === 0)
			return result;		

		// Gère le premier événement différemment
		var firstEvent = events[0];
		if (!firstEvent.allDay && !beginAtStartOfDay(firstEvent)) {
			result.push(createGhostEvent(getStartOfDay(firstEvent.startTime), firstEvent.startTime));
		}
		result.push(firstEvent);

		// Parcourt le reste des événements
		for  (var i = 1; i < events.length; i++) {
			var previousEvent = events[i - 1]; // déjà ajouté dans result
			var currentEvent = events[i];
			var beginsAtStartOfDay = beginAtStartOfDay(currentEvent);
			var dayChanged = currentEvent.startTime.dayOfYear() !== previousEvent.startTime.dayOfYear()
				|| currentEvent.endTime.dayOfYear() !== previousEvent.endTime.dayOfYear();

			// Pas besoin d'insérer d'événement dans les conditions suivantes :
			if (currentEvent.allDay									// si l'événement courrant est sur la journée entière
				|| beginsAtStartOfDay								// si l'événement courrant commence au début de la journée
				|| (areOverlapping(previousEvent, currentEvent) && !previousEvent.allDay)) {	// si les 2 événements se chevauchent (ou sont collés) et que l'événement précédent n'est pas sur la journée entière
				result.push(currentEvent);
				continue;
			}

			var startTime = previousEvent.endTime;
			var endTime = currentEvent.startTime;

			// Créneau libre entre le début de la journée et le premier événement
			if (dayChanged || previousEvent.allDay) {
				startTime = moment(currentEvent.startTime.valueOf());
				startTime.hours(self.dayStart.hours());
				startTime.minutes(self.dayStart.minutes());
			}

			result.push(createGhostEvent(startTime, endTime));
			result.push(currentEvent);
		}
		return result;
	};

	BBMCalendarPlanner.prototype.generateCell = function(cell, list) {
		
		function linkMessageToSpanClickAndShowMessage(cell, showErrorFunction, listMessage) {
			if (!showErrorFunction)
				return;
			let availableMessages = ['not-enought-break-time', 'too-many-working-time-per-day'],
				messages = [];
			availableMessages.forEach(key => {
				let el = cell.find('.' + key),
					message = listMessage[key]; 
				if (!!message && !!el) {
					el.on('click', function() { showErrorFunction([message]); });
					messages.push(message);
				}
			});
			if (messages.length > 0)
				showErrorFunction(messages);
		}
		
		function getSubtitle(notEnoughtBreakTime, tooManyWorkingTimePerDay, subtitleText) {
			let divSubtitle = $('<div />'),
				subtitle = $('<span />').text(subtitleText);
			if (!!notEnoughtBreakTime) {
				divSubtitle.append($('<span class="material-icons text-danger not-enought-break-time" />'));
			}
				
			if (!!tooManyWorkingTimePerDay)
				divSubtitle.append($('<span class="material-icons text-danger too-many-working-time-per-day" />'));
			
			return (!!notEnoughtBreakTime || !!tooManyWorkingTimePerDay) 
				? divSubtitle.addClass("subtitle").append(subtitle.css({position: "relative"})) 
				: subtitle.addClass("subtitle");
		}
		
		// Récupérer l'objet permettant d'ajuster en fonction de la langue
		var lang = CalendarAPI.getLocale(moment.locale());
		var self = this;
		var dateAttributeFormat = CalendarAPI.dateAttributeFormat;
		var day = cell.attr('data-date');
		var momentDay = moment(day, dateAttributeFormat);
		var canInsert = self.isinsertable(momentDay);
		cell.toggleClass('insertable', canInsert);
		
		if (!!self.lastEventEndTimePreviousDay && 
			((self.lastEventEndTimePreviousDay + 24 * 3600 * 1000) < moment(day).valueOf()) 
			|| (self.lastEventEndTimePreviousDay > moment(day).valueOf()))
			self.lastEventEndTimePreviousDay = undefined;
	
		if (cell.parent().is('.total')) {
	
			// 1. TOTAUX PAR JOUR
			// Calculer les totaux event et totalDuration du jour
			var eventCount = 0;
			var totalDuration = 0;
			var firstEventStartTime = Number.MAX_SAFE_INTEGER;
			var lastEventEndTime = Number.MIN_SAFE_INTEGER;
			var allDay = false;
			let notEnoughtBreakTime, tooManyWorkingTimePerDay, messages = {};
			list.filter(function(entry) {
				return !entry.ghost && (!self.totalFilterFunction || self.totalFilterFunction(entry));
			}).forEach(function(entry) {
				if (entry.startTime.format(dateAttributeFormat) <= day
							&& entry.endTime.format(dateAttributeFormat) >= day) {
					if (entry.allDay)
						allDay = true;
					
					eventCount++;
					var duration;
					if (entry.startTime.format(dateAttributeFormat) <= entry.endTime.format(dateAttributeFormat)) {
						// L'évènement s'étale sur plusieurs jours
						if (entry.startTime.format(dateAttributeFormat) < entry.endTime.format(dateAttributeFormat)) {
							var currentDay = moment(day).hours('00').minutes('00');
							if (entry.startTime.format(dateAttributeFormat) === day) {
								duration = moment(currentDay).add(1, 'days').diff(moment(entry.startTime));
								firstEventStartTime = Math.min(firstEventStartTime, entry.startTime).valueOf();
								lastEventEndTime = moment(currentDay).add(1, 'days').valueOf();
							} else if (entry.startTime.format(dateAttributeFormat) < day && entry.endTime.format(dateAttributeFormat) > day) {
								duration = 24 * 3600 * 1000; // 24 h
								firstEventStartTime = currentDay.valueOf();
								lastEventEndTime = moment(currentDay).add(1, 'days').valueOf();
								allDay = true;
							} else { // entry.startTime.format(dateAttributeFormat) < day && entry.endTime.format(dateAttributeFormat) === day
								duration = moment(entry.endTime).diff(currentDay);
								firstEventStartTime = currentDay.valueOf();
								lastEventEndTime = Math.max(lastEventEndTime, entry.endTime.valueOf());
							}
						}
						else {
							duration = moment(entry.endTime).diff(moment(entry.startTime));
							firstEventStartTime = Math.min(firstEventStartTime, entry.startTime.valueOf());
							lastEventEndTime = Math.max(lastEventEndTime, entry.endTime.valueOf());
						}
					}
					else {
						duration = entry.endTime.diff(entry.startTime);
						firstEventStartTime = Math.min(firstEventStartTime, entry.startTime.valueOf());
						lastEventEndTime = Math.max(lastEventEndTime, entry.endTime.valueOf());
					}
					totalDuration += duration || 0;
				}
			});
			if (totalDuration > 0 && (self.maxWorkingTimePerDay || self.minBreakTimeBetweenWorkingDays)) {
				if (!!self.lastEventEndTimePreviousDay && !!self.minBreakTimeBetweenWorkingDays) { //on calcule si on a assez de repos
					notEnoughtBreakTime = firstEventStartTime - self.lastEventEndTimePreviousDay < self.minBreakTimeBetweenWorkingDays * 3600 * 1000;
					if (!!notEnoughtBreakTime)
						messages['not-enought-break-time'] = lang.notEnoughtBreakTimeFormatMessage(self.minBreakTimeBetweenWorkingDays, momentDay.valueOf());
				}
				if (!!self.maxWorkingTimePerDay) {
					tooManyWorkingTimePerDay = (lastEventEndTime - firstEventStartTime) > self.maxWorkingTimePerDay * 3600 * 1000;
					if (!!tooManyWorkingTimePerDay)
						messages['too-many-working-time-per-day'] = lang.tooManyWorkingTimePerDayMessage(self.maxWorkingTimePerDay, momentDay.valueOf());
				}
				self.lastEventEndTimePreviousDay = lastEventEndTime;
			}
			// Vider la cellule
			cell.empty();
			if (eventCount !== 0) {
				// Indiquer que le jour contient des données
				cell.removeClass('empty');
				//1.a EN VUE MOIS
				if (cell.closest('.calendar').is('.month')) {
					cell.append($(self.editTemplate));
					if (cell.closest('.total').is(':last-child'))
						cell.find('.dropdown').removeClass('dropdown').addClass('dropup');
					// si on est en mois, on construit la dropdown list
					// Ajouter le bouton de consultation / modification
					var dropdownMenu = cell.find('ul.dropdown-menu');
					list.filter(function(entry) {
						return !entry.ghost;
					}).forEach(function(entry) {
						if (entry.startTime.format(dateAttributeFormat) <= day
									&& entry.endTime.format(dateAttributeFormat) >= day) {
							var startTime;
							var endTime;
							if (! entry.allDay) {
								startTime  = (entry.startTime.format(dateAttributeFormat) === day) ? lang.formatTime(entry.startTime.valueOf()) : lang.formatTime(self.dayStart.valueOf());
								endTime = (entry.endTime.format(dateAttributeFormat) === day) ? lang.formatTime(entry.endTime.valueOf()) : lang.formatTime(self.dayEnd.valueOf());
							}
							var type = self.types[entry.typeIndex];
							var liElement = $(self.dropdownLiTemplate);
							liElement.find('span').css('background-color', type ? type.color : '#eeeeee');
							liElement.find('b').text(entry.allDay ? lang.wholeDayLabel : startTime + ' - ' + endTime + ' '); // si on est en journée entière on affiche 'Toute la journée'
							liElement.find('a').append(entry.caption);
							liElement.attr('data-id', entry.id);
							liElement.data('event', entry);
							dropdownMenu.append($(liElement));
						}
					});
	
					if (canInsert)
						dropdownMenu.append($('<li/>').append($('<a class="editevent"/>').append($(self.addInDropdownTemplate)).append(' ' + lang.addEventLabel)));
				

					var timeRangeStart = lang.formatTime(firstEventStartTime);
					var timeRangeEnd = lang.formatTime(lastEventEndTime);
					var timeRange = allDay ? lang.wholeDayLabel : timeRangeStart + ' - ' + timeRangeEnd;
					// Ajouter le nombre d'event en grand
					cell.append($('<span class="title" />').text(timeRange))
						// Ajouter le temps total en plus petit
						.append(getSubtitle(notEnoughtBreakTime, tooManyWorkingTimePerDay, CalendarAPI.formatDuration(lang, totalDuration)));
					
					linkMessageToSpanClickAndShowMessage(cell, self.showError, messages);
				}
				// 1.b EN VUE JOUR
				else {
					// Ajouter le bouton d'ajout
					if (canInsert)
						cell.append($(self.addTemplate));
					// Ajouter le nombre d'event en grand
					cell.append($('<span class="title" />').text(lang.formatEventCount(eventCount)))
					// Ajouter le temps total en plus petit et si il y a des alertes de dépassement de temps de travail ou de temps de repos trop court entre deux journées.
						.append(getSubtitle(notEnoughtBreakTime, tooManyWorkingTimePerDay, CalendarAPI.formatDuration(lang, totalDuration)));

					linkMessageToSpanClickAndShowMessage(cell, self.showError, messages);

					if (self.extraSubtitleTemplate) {
						let template = self.extraSubtitleTemplate(day);
						if (template.then) { // S'agit-il d'une deferred ?
							template.then(t => {
								cell.append(t);
								cell.parent().addClass('total-with-extra');
							});
						}
						else {
							cell.append(self.extraSubtitleTemplate(day));
							cell.parent().addClass('total-with-extra');
						}
					}

				}
			} else {
				// Indiquer que le jour est pour l'instant vide
				cell.addClass('empty');
	
				// Ajouter un bouton aux jours vides, à condition qu'ils soient modifiables
				if (canInsert)
					cell.append($(self.addTemplate));
			}
		}
		else if (cell.closest('.calendar').is('.month')) {
			// 2. DETAIL EN VUE MOIS
	
			// Vider la cellule en conservant tout de même le numéro du jour
			cell.children(':not(.day)').remove();
	
			// Calculer le nombre d'event et duration par type de event
			list.filter(function(entry) {
				return !entry.ghost;
			}).forEach(function(entry) {
				if (entry.startTime.format(dateAttributeFormat) <= day
							&& entry.endTime.format(dateAttributeFormat) >= day) {
					var type = self.types[entry.typeIndex];
					var duration;
					if (entry.startTime.format(dateAttributeFormat) < entry.endTime.format(dateAttributeFormat)) {
						duration = moment(entry.endTime).diff(moment(entry.startTime)); 
					}
					else {
						duration = entry.endTime.diff(entry.startTime);
					}
					duration = duration ? CalendarAPI.formatDuration(lang, duration) : '';
	
					var title = entry.caption + (entry.caption && duration ? ' (' + duration + ')': '');
					cell.find('span').first().after($('<button class="plannerEvent" />')
						.css('background-color', type ? type.color : '#eeeeee')
						.attr('title', title)
						.attr('data-id', entry.id)
						.data('event', entry));
				}
			});
		}
		else {
			// 3. DETAIL EN VUE JOUR(s)
			cell.empty();
	
			// On va calculer une fois seulement l'heure de début et l'heure de fin de journée pour ce jour 
			var dayStartMoment = moment(day, dateAttributeFormat); //00h00 du jour
			var dayEndMoment = moment(day, dateAttributeFormat).add(1, 'days'); // 00h00 du jour + 1
	
			// Pour la détection des erreurs, on conserve les event et div parcourus
			var eventDivs = [];
			var eventEndMoments = [];
	
			var i = 0;
			while (i < list.length) {
				var event = list[i]; 
				var startDay = event.startTime.format(dateAttributeFormat);
				var endDay = event.endTime.format(dateAttributeFormat);
				if (startDay <= day && endDay >= day) {
					var type = self.types[event.typeIndex];
					if (event.ghost) {
						type = {
							color: 'transparent'
						};
					}
	
					var startMoment = moment(event.startTime);
					var startText = lang.formatTime(((startDay === day) ? startMoment : dayStartMoment).valueOf());
					var endMoment = moment(event.endTime);
					var endText = lang.formatTime(((endDay === day) ? endMoment : dayEndMoment).valueOf());
	
					var duration = event.allDay ? '' : endMoment.diff(startMoment);
					var minSize = 40 /*trait journée entière + caption */;
					var divSize;
					if (self.fixedEventSize && event.allDay) {
						divSize = minSize + self.properties.length * 20; // 20px par propriétés en plus
					}
					else if (self.fixedEventSize && !event.allDay) {
						divSize = minSize + (self.properties.length + 1) * 20; // 20px par propriétés en plus + 20 pour l'horraire
					}
					else if (!self.fixedEventSize && event.allDay) {
						divSize = minSize;
					}
					else {
						divSize = self.showGhostEvents ? duration / 60000 : Math.max(duration / 60000, minSize + 20); // 20px pour l'horraire
					}
					
					var title = event.caption + (duration ? ' (' + CalendarAPI.formatDuration(lang, duration) + ')' : '');
	
					// Détecter les conflits
					var error = false;
					for (var j = 0; j < eventEndMoments.length; j++) {
						if (eventEndMoments[j].isAfter(startMoment)) {
							eventDivs[j].find('.event-hours').addClass('text-danger');
							error = true;
						}
					}
	
					var eventDiv = $('<div class="plannerEvent" />')
						.toggleClass('event-ghost', !!event.ghost)
						.toggleClass('event-hidden', !!event.ghost && !self.showGhostEvents)
						.toggleClass('event-allday', !!event.allDay)
						.toggleClass('event-done', !!event.done)
						.toggleClass('event-locked', !!event.locked)
						.toggleClass('event-warning', !!event.warning)
						.data('event', event)
						.append($('<div class="event-color" />').css('background-color', type ? type.color : '#eeeeee'))
						.css('height', divSize + 'px')
						.appendTo(cell);

					if (event.ghost) {
						eventDiv = eventDiv.attr('title', lang.freeSlot)
							.append($('<div class="event-caption" />').text(lang.freeSlot))
							.css('background-color', '#e1f1fa');
					}

					if (!event.ghost) {
						eventDiv = eventDiv.attr('title', title).attr('data-id', event.id)
							.append($('<div class="event-hours" />')
								.toggleClass('text-danger', error)
								.text(event.allDay ? '' : startText + ' - ' + endText))
							.append($('<div class="event-caption" />')
							.text(event.caption));

						for (var pi = 0; pi < self.properties.length; pi++) {
							var property = self.properties[pi];
							var content = property.format(event, type, lang);
							if (! content)
								continue;
							var div = $('<div />').css('white-space', property.canWrap ? 'pre' : '').appendTo(eventDiv);
							if (typeof content === 'string')
								div.text(content);
							else
								div.append(content);
							// div.attr('title', property.caption);
						}
					}

					eventDivs.push(eventDiv);
					eventEndMoments.push(endMoment);
				}
				i++;
			}

			// On modifie la taille des événements en fonction de l'affichage est événements fantomes:
			//     - taille minimale si on ne les affiche pas
			//     - pas de taille mnimum sinon
			self.target.on('calendar.showghostchanged', function() {
				$(self.target).find('.plannerEvent').each(function(_index, eventDiv) {
					var ev = $(eventDiv).data('event');
					var duration = moment(ev.endTime).diff(moment(ev.startTime));
					var height = self.showGhostEvents ? duration / 60000 : Math.max(duration / 60000, minSize + 20);
					$(eventDiv).css('height', height + 'px');
				});
			});
		}
	};

	BBMCalendarPlanner.prototype.toggleGhostEvents = function(show) {
		this.showGhostEvents = show;
		this.target.find('.event-ghost').toggleClass('event-hidden', !show);
		this.target.trigger('calendar.showghostchanged');
		this.target.calendar().internalChange();
	};

	function BBMCalendar(target, options) {
		this.target = $(target);
		this.view = CalendarAPI.getView(options.view || 'fweek');
		this.moment = options.moment || moment();
		this.workedDays = options.workedDays || [false, true, true, true, true, true, false];
		this.specialDays = options.specialDays || [];
		this.showScrollBar = !!options.showScrollBar;
		this.showScrollDirections = !!options.showScrollDirections;
		var implementation;
		if (options.expenses)
			implementation = new BBMCalendarExpense(this.target, options.expenses);
		else if (options.activity)
			implementation = new BBMCalendarActivity(this.target, options.activity);
		else
			implementation = new BBMCalendarPlanner(this.target, options.planner);
		this.content = implementation; // chaque implémentation fournit "rowClass", "queryData" et "generateCell"
		this.internalChange();
	}

	BBMCalendar.prototype.refresh = function() {
		var div = $('<div class="calendar" />').addClass(this.view.name).appendTo(this.target.empty()),
			headerDiv = $('<div class="header" />').appendTo(div),
			when = moment(this.startMoment),
			self = this,
			m, s, i, monthDiv, div1, div2, dayCaption, bgcolor, sd;
	
		// Méthode recherchant un éventuel jours spécial la date "m"
		function findSpecialDay(m, specialDays) {
			return specialDays.find(function(sd){
				var sdMoment = moment(sd.specialDate);
				return sdMoment.date() === m.date() && sdMoment.month() === m.month()
					&& (sd.allYears || sdMoment.year() === m.year());
			});
		}
	
		if (self.view.singleWeek) {
			// En-têtes des jours
			do {
				if (!self.view.filterDays || self.workedDays[when.day()]) {
					sd = findSpecialDay(when, self.specialDays);
	
					if (sd) {
						dayCaption = '[<span>' + sd.caption + '</span><span>]DD[</span>]';
						bgcolor = 'background-color: #cecece;';
					} else {
						dayCaption = '[<span>]dddd[</span><span>]DD[</span>]';
						bgcolor = '';
					}
					
					dayCaption = when.format(dayCaption);				
					$('<div style="' + bgcolor +'"/>').appendTo(headerDiv).append(dayCaption);
				}
					
				when.add(1, 'days');
			} while (when.isSameOrBefore(self.endMoment, 'days'));
	
			// Génération les cellules, vides le temps de l'appel asynchrone
			div1 = $('<div class="' + self.content.rowClass +'" />').appendTo(div)
				.wrap('<div class="detail" />') /*pour, lorsqu'il y a un scroll, que la div de la journée aille jusqu'en bas du scroll, on ajoute un div intermédiaire */;
			div2 = $('<div class="' + self.content.rowClass +' total" />').appendTo(div);
			m = moment(self.startMoment);
			do {
				if ((self.view.filterDays && self.workedDays) ? self.workedDays[m.day()] : true) {
					s = m.format(CalendarAPI.dateAttributeFormat);
					$('<div />').attr('data-date', s).appendTo(div1);
					$('<div />').attr('data-date', s).appendTo(div2).addClass('empty');
				}
				m.add(1, 'days');
			} while (m.isSameOrBefore(self.endMoment, 'days'));
	
		} else {
			// En-têtes des jours
			var weekdays = moment.weekdays(),
				firstDay = self.startMoment.day();
			for (i = 0; i < 7; i++) {
				$('<div />').appendTo(headerDiv).append('<span>' + weekdays[(firstDay + i) % 7] + '</span>');
			}
	
			// Génération les cellules, vides le temps de l'appel asynchrone
			monthDiv = $('<div class="multi" />').appendTo(div);
			m = moment(self.startMoment);
			do {
				div1 = $('<div class="' + self.content.rowClass +' detail" />').appendTo(monthDiv);
				div2 = $('<div class="' + self.content.rowClass +' total" />').appendTo(monthDiv);
				for (i = 0; i < 7; i++) {
					// special days
					sd = findSpecialDay(m, self.specialDays);
					if (sd)
						dayCaption = 'DD[</span> <span class="day" style="font-size: 0.8em">' + sd.caption + ']';
					else
						dayCaption = 'DD';
						
					// affichage
					s = m.format(CalendarAPI.dateAttributeFormat);
					$('<div />').attr('data-date', s).appendTo(div1)
						.append('<span class="day">' + m.format(dayCaption) + '</span>')
						.toggleClass('disabled', m.month() !== self.moment.month());
					$('<div />').attr('data-date', s).appendTo(div2)
						.addClass('empty');
					m.add(1, 'days');
				}
			} while (m.isSameOrBefore(self.moment, 'month'));
		}
	
		// Ecouter le scroll pour indiquer visuellement s'il reste des données hors champ
		if (self.showScrollDirections)
			(monthDiv || div1.parent()).on('scroll', self.internalScroll.bind(self));
	
		// Récupérer les données de manière asynchrone
		self.content.queryData(self.startMoment, self.endMoment, function(data) {
			div.find('div.' + self.content.rowClass +' > div').each(function() {
				self.content.generateCell($(this), data);
			});
			// Simuler un scroll pour indiquer visuellement s'il reste des données hors champ
			if (self.showScrollBar || self.showScrollDirections)
				self.checkScroll();
		});
	};

	BBMCalendar.prototype.setView = function(name) {
		this.view = CalendarAPI.getView(name || 'fweek');
		this.internalChange();
	};

	BBMCalendar.prototype.setMoment = function(moment) {
		this.moment = moment;
		this.internalChange();
	};

	BBMCalendar.prototype.navigateBackward = function() {
		this.moment.add(-1, this.view.navigateUnit);
		this.internalChange();
	};

	BBMCalendar.prototype.navigateForeward = function() {
		this.moment.add(1, this.view.navigateUnit);
		this.internalChange();
	};

	BBMCalendar.prototype.checkScroll = function(eventTargetOrNull) {
		var calendar = this.target.children('.calendar');
		var scrollable = eventTargetOrNull || (this.view.name === 'month' ? calendar.children('.multi')[0] : calendar.children('.detail')[0]);
		var scrollTop = scrollable.scrollTop;
		var scrollHeight = scrollable.scrollHeight;
		var offsetHeight = scrollable.offsetHeight;
		var hasBefore = scrollTop > 0;
		var hasAfter = scrollHeight - offsetHeight - scrollTop > 0;
		//console.log('scrollTop', scrollTop, 'scrollHeight', scrollHeight, 'offsetHeight', offsetHeight);
		calendar
			.toggleClass('with-scrolls', this.showScrollBar && (hasBefore || hasAfter))
			.toggleClass('with-something-before', this.showScrollDirections && hasBefore)
			.toggleClass('with-something-after', this.showScrollDirections && hasAfter);
	};

	BBMCalendar.prototype.internalScroll = function(event) {
		this.checkScroll(event.target);
	};

	BBMCalendar.prototype.internalChange = function() {
		// Ajuster la période
		this.startMoment = this.view.startMoment(this.moment, this.workedDays);
		this.endMoment = this.view.endMoment(this.moment, this.workedDays);

		var value = {
			moment: this.moment,
			view: this.view,
			period: this.view.formatPeriod(this.moment, this.startMoment, this.endMoment),
			start: this.startMoment,
			end: this.endMoment
		};

		if (this.content.showGhostEvents !== undefined)
			value.showGhostEvents = this.content.showGhostEvents;

		// Prévenir les listeners (pour afficher quelque part la période par exemple)
		this.target.trigger('calendar.change', [value]);
		// Mettre à jour la table
		this.refresh();
	};

	$.fn.calendar = function(action, params) {
		// Récupération du BBMCalendar
		if (typeof action === 'undefined') {
			if (this.length === 1)
				return this[0].calendar;
			return undefined;
		}
		// Construction
		if (typeof action === 'object') {
			var options = action;
			return this.each(function() {
				this.calendar = new BBMCalendar(this, options);
			});
		}
		// Destruction
		if (action === 'destroy') {
			return this.each(function() {
				delete this.calendar;
				this.innerHTML = '';
			});
		}
		// Helper methods
		return this.each(function() {
			this.calendar[action](params);
		});
	};

	var api = {
		// Format pour stocker les dates dans l'attribut
		dateAttributeFormat: 'YYYYMMDD',
		// Récupération d'une locale parmi celles qui sont "enregistrées"
		getLocale: function(name) { return locales[name]; },
		// Enregistrement d'une nouvelle locale en dehors des locales supportées nativement (fr, en, de) 
		addLocale: function(name, locale) { locales[name] = locale; },
		// Récupération d'une vue parmi celles qui sont "enregistrées"
		getView: function(name) { return views[name]; },
		// Enregistrement d'une nouvelle vue en dehors des vues supportées nativement (day, wweek, fweek, month) 
		addView: function(name, view) { views[name] = view; },
		// Méthode (lang, ms)=>string formatant en texte une durée en millisecondes "ms" dans la langue "lang"
		formatDuration: formatDuration
	};

	return api;
}

var CalendarAPI = buildCalendarAPI(window.jQuery, window.moment);
